/**
 * Type definitions for POS.
 * These interfaces, aliases, or model helpers describe the shared data contract used
 * by the surrounding feature or UI layer.
 */
import { AddressDetails, DEFAULT_ADDRESS_DETAILS } from '../../shared/models/address.types';
import { ContactDetails, DEFAULT_CONTACT_PHONE } from '../../shared/models/contact.types';

export type PosProfile = 'Cabine' | 'Level 2';
export type PosType = 'High Value' | 'Regular';
export type PosStatus = 'active' | 'inactive';
export type PosOperationalStatus = 'active' | 'inactive';
export type PosStockStatus = 'ok' | 'low' | 'out-of-stock';
export type CreationStatus = 'pending' | 'in-progress' | 'approved' | 'rejected' | 'done';
export type PosStockOperation = 'credit' | 'debit';

export interface PosStockEntryItem {
  categoryId: string;
  categoryName: string;
  productId: string;
  productName: string;
  variantId: string;
  variantName: string;
  quantity: number;
}

export interface PosStockDetails {
  stockOnHand: number;
  reorderLevel: number;
  stockStatus: PosStockStatus;
  lastStockUpdate: string;
  notes: string;
  entries: PosStockEntry[];
}

export interface PosStockEntry {
  id: string;
  dateTime: string;
  date?: string;
  operation?: PosStockOperation;
  items?: PosStockEntryItem[];
  notes?: string;
  agent?: string;
  productAsset?: string;
  categoryId?: string;
  categoryName?: string;
  productId?: string;
  variantId?: string;
  productName?: string;
  variantName?: string;
  isCashProduct?: boolean;
  sellingPrice?: number | null;
  quantity: number;
  proofRef?: string;
  balanceAfter?: number;
  entryStatus?: string;
  remarks?: string;
}

export interface ProspectStockTransactionItem {
  categoryId: string;
  products: Array<{
    productId: string;
    quantity?: number;
    variants?: Array<{ variantId: string; quantity: number }>;
  }>;
}

export interface ProspectStockTransactionPayload {
  operation: 'SET' | 'ADD' | 'SUBTRACT';
  items: ProspectStockTransactionItem[];
  remarks?: string;
  proofRef?: string;
  dateTime: string;
  notes: string;
  reorderLevel?: number;
}

export type PosStockTransactionPayload = ProspectStockTransactionPayload;

export interface PosDocument {
  id: string;
  name: string;
  type: 'document' | 'image';
  url: string;
  originalImage?: string;
  mimeType?: string;
  uploadedAt: string;
}

export interface PosUpsertPayload {
  name: string;
  personalNumber: string;
  posNumber: string;
  profile: PosProfile;
  posType: PosType;
  status: PosStatus;
  operationalStatus: PosOperationalStatus;
  primaryAgencyId?: string;
  averageRestocking: number;
  contact: ContactDetails;
  address: AddressDetails;
  stockDetails: PosStockDetails;
  documents: PosDocument[];
}

export interface PosModel {
  id: string;
  name: string;
  personalNumber: string;
  posNumber: string;
  code?: string;
  profile: PosProfile;
  posType: PosType;
  status: PosStatus;
  operationalStatus: PosOperationalStatus;
  creationStatus: CreationStatus;
  stockStatus: PosStockStatus;
  averageRestocking: number;
  entityType: 'External POS';
  assignedAgentAuthUserId?: string;
  assignedAgentName?: string;
  primaryAgent?: string;
  primaryAgency?: string;
  primaryAgencyId?: string;
  lastVisitDate?: string;
  createdDate: string;
  contact: ContactDetails;
  contactPhoneDisplay: string;
  address: AddressDetails;
  city: string;
  area: string;
  stockDetails: PosStockDetails;
  stockOnHandDisplay: string;
  documents: PosDocument[];
}

export interface PosRecord extends PosModel {}

export interface ProspectUpsertPayload {
  name: string;
  personalNumber: string;
  posNumber: string;
  profile: PosProfile;
  posType: PosType | '';
  status: PosStatus;
  averageRestocking: number;
  creationStatus: CreationStatus;
  contact: ContactDetails;
  address: AddressDetails;
  stockDetails: PosStockDetails;
  documents: PosDocument[];
}

export interface ProspectModel {
  id: string;
  name: string;
  personalNumber: string;
  posNumber: string;
  profile: PosProfile;
  posType: PosType | '';
  status: PosStatus;
  operationalStatus: PosOperationalStatus;
  averageRestocking: number;
  creationStatus: CreationStatus;
  assignedAgentAuthUserId: string;
  assignedAgentName: string;
  contact: ContactDetails;
  contactPhoneDisplay: string;
  address: AddressDetails;
  city: string;
  area: string;
  createdDate: string;
  stockDetails: PosStockDetails;
  documents: PosDocument[];
}

export interface ProspectRecord extends ProspectModel {}

export const DEFAULT_POS_PROFILE: PosProfile = 'Cabine';
export const DEFAULT_POS_TYPE: PosType = 'Regular';
export const DEFAULT_POS_STATUS: PosStatus = 'active';
export const DEFAULT_POS_OPERATIONAL_STATUS: PosOperationalStatus = 'active';
export const DEFAULT_CREATION_STATUS: CreationStatus = 'in-progress';
export const DEFAULT_POS_STOCK_DETAILS: PosStockDetails = {
  stockOnHand: 0,
  reorderLevel: 0,
  stockStatus: 'ok',
  lastStockUpdate: '',
  notes: '',
  entries: []
};

export const DEFAULT_POS_ADDRESS = DEFAULT_ADDRESS_DETAILS;
export const DEFAULT_POS_PHONE = DEFAULT_CONTACT_PHONE;

/**
 * BUSINESS LOGIC: toPosRecord
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
export function toPosRecord(id: string, payload: PosUpsertPayload, current?: PosRecord): PosRecord {
  const stockDetails = normalizePosStockDetails(payload.stockDetails);
  return {
    id: current?.id ?? id,
    name: payload.name,
    personalNumber: payload.personalNumber,
    posNumber: payload.posNumber,
    profile: payload.profile,
    posType: payload.posType,
    status: payload.status,
    operationalStatus: payload.operationalStatus ?? current?.operationalStatus ?? DEFAULT_POS_OPERATIONAL_STATUS,
    creationStatus: current?.creationStatus ?? 'approved',
    averageRestocking: payload.averageRestocking,
    assignedAgentAuthUserId: current?.assignedAgentAuthUserId ?? '',
    assignedAgentName: current?.assignedAgentName ?? '',
    primaryAgencyId: payload.primaryAgencyId ?? current?.primaryAgencyId,
    primaryAgency: current?.primaryAgency,
    stockStatus: stockDetails.stockStatus,
    entityType: 'External POS',
    createdDate: current?.createdDate ?? new Date().toISOString().slice(0, 10),
    contact: payload.contact,
    contactPhoneDisplay: formatPosPhoneDisplay(payload.contact),
    address: payload.address,
    city: payload.address.locality,
    area: payload.address.area,
    stockDetails,
    stockOnHandDisplay: `${stockDetails.stockOnHand} units`,
    documents: payload.documents ?? []
  };
}

/**
 * BUSINESS LOGIC: toProspectRecord
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
export function toProspectRecord(id: string, payload: ProspectUpsertPayload, current?: ProspectRecord): ProspectRecord {
  const stockDetails = normalizePosStockDetails(payload.stockDetails ?? current?.stockDetails ?? DEFAULT_POS_STOCK_DETAILS);
  return {
    id: current?.id ?? id,
    name: payload.name,
    personalNumber: payload.personalNumber,
    posNumber: payload.posNumber,
    profile: payload.profile,
    posType: payload.posType,
    status: payload.status,
    operationalStatus: current?.operationalStatus ?? DEFAULT_POS_OPERATIONAL_STATUS,
    averageRestocking: payload.averageRestocking,
    creationStatus: payload.creationStatus,
    assignedAgentAuthUserId: current?.assignedAgentAuthUserId ?? '',
    assignedAgentName: current?.assignedAgentName ?? '',
    contact: payload.contact,
    contactPhoneDisplay: formatPosPhoneDisplay(payload.contact),
    address: payload.address,
    city: payload.address.locality,
    area: payload.address.area,
    createdDate: current?.createdDate ?? new Date().toISOString().slice(0, 10),
    stockDetails,
    documents: payload.documents ?? []
  };
}

/**
 * BUSINESS LOGIC: normalizePosStockDetails
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
export function normalizePosStockDetails(value: Partial<PosStockDetails> | null | undefined): PosStockDetails {
  const details = value ?? DEFAULT_POS_STOCK_DETAILS;
  const entries = Array.isArray(details.entries) ? details.entries.map(normalizePosStockEntry) : [];
  const stockOnHand = Number.isFinite(details.stockOnHand) ? Number(details.stockOnHand) : entries.reduce((sum, entry) => sum + entry.quantity, 0);
  const lastStockUpdate = String(details.lastStockUpdate ?? '').trim() || entries[0]?.dateTime?.slice(0, 10) || '';

  return {
    stockOnHand,
    reorderLevel: Number(details.reorderLevel ?? 0) || 0,
    stockStatus: details.stockStatus ?? 'ok',
    lastStockUpdate,
    notes: String(details.notes ?? '').trim(),
    entries
  };
}

/**
 * BUSINESS LOGIC: normalizePosStockEntry
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
export function normalizePosStockEntry(value: Partial<PosStockEntry> | null | undefined): PosStockEntry {
  const raw = value ?? {};
  const dateTime = String(raw.dateTime ?? raw.date ?? '').trim() || new Date().toISOString();
  const items = Array.isArray(raw.items) && raw.items.length > 0
    ? raw.items.map((item) => normalizePosStockEntryItem(item))
    : [legacyPosStockEntryItem(raw)];
  const quantity = Number(raw.quantity) > 0 ? Number(raw.quantity) : items.reduce((sum, item) => sum + item.quantity, 0);

  return {
    id: String(raw.id ?? crypto.randomUUID()),
    dateTime,
    date: String(raw.date ?? '').trim() || dateTime.slice(0, 10),
    operation: raw.operation === 'debit' ? 'debit' : 'credit',
    items,
    notes: String(raw.notes ?? raw.remarks ?? '').trim(),
    quantity,
    // Preserve category/product/variant identity so the stock cards can label and group entries
    // by their stored category name instead of falling back to "N/A" (issue #5/#7).
    categoryId: String(raw.categoryId ?? '').trim() || undefined,
    categoryName: String(raw.categoryName ?? '').trim() || undefined,
    productId: String(raw.productId ?? '').trim() || undefined,
    variantId: String(raw.variantId ?? '').trim() || undefined,
    productName: String(raw.productName ?? '').trim() || undefined,
    variantName: String(raw.variantName ?? '').trim() || undefined,
    proofRef: String(raw.proofRef ?? '').trim() || undefined,
    balanceAfter: Number.isFinite(raw.balanceAfter) ? Number(raw.balanceAfter) : undefined,
    agent: String(raw.agent ?? '').trim() || undefined,
    productAsset: String(raw.productAsset ?? '').trim() || undefined,
    entryStatus: String(raw.entryStatus ?? '').trim() || undefined,
    remarks: String(raw.remarks ?? '').trim() || undefined
  };
}

/**
 * BUSINESS LOGIC: normalizePosStockEntryItem
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
function normalizePosStockEntryItem(value: Partial<PosStockEntryItem> | null | undefined): PosStockEntryItem {
  const raw = value ?? {};
  const quantity = Number(raw.quantity) > 0 ? Number(raw.quantity) : 0;

  return {
    categoryId: String(raw.categoryId ?? '').trim(),
    categoryName: String(raw.categoryName ?? '').trim(),
    productId: String(raw.productId ?? '').trim(),
    productName: String(raw.productName ?? '').trim(),
    variantId: String(raw.variantId ?? '').trim(),
    variantName: String(raw.variantName ?? '').trim(),
    quantity
  };
}

/**
 * BUSINESS LOGIC: legacyPosStockEntryItem
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
function legacyPosStockEntryItem(value: Partial<PosStockEntry>): PosStockEntryItem {
  const fallbackName = String(value.productAsset ?? 'Manual stock item').trim() || 'Manual stock item';

  return {
    categoryId: '',
    categoryName: 'Uncategorized',
    productId: '',
    productName: fallbackName,
    variantId: '',
    variantName: fallbackName,
    quantity: Number(value.quantity ?? 0) || 0
  };
}

/**
 * BUSINESS LOGIC: formatPosPhoneDisplay
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
 */
export function formatPosPhoneDisplay(contact: ContactDetails): string {
  const dialCode = contact.phone.dialCode ? `+${contact.phone.dialCode}` : '';
  return [dialCode, contact.phone.number].filter(Boolean).join(' ').trim();
}
